import {
  APIComponentAttributes,
  APIDiffContext,
  APIPresentationScenarioAttributes,
  APIReferenceAttributes,
  APIScopeData,
  ArdoqId,
} from '@ardoq/api-types';
import { logError } from '@ardoq/logging';
import { partition } from 'lodash';

export const fixPresentationScenario = (
  scenario: APIPresentationScenarioAttributes
) => {
  const { _id, scope, diffContext } = scenario;
  return {
    ...scenario,
    scope: removeDanglingReferencesInScopeData(scope, _id),
    diffContext: fixDiffContext(diffContext, _id),
  };
};

export const fixDiffContext = (
  diffContext: APIDiffContext,
  scenarioId: ArdoqId | null
) => {
  const { master, branch, branchOff } = diffContext;
  return {
    master: removeDanglingReferencesInScopeData(master, scenarioId),
    branch: removeDanglingReferencesInScopeData(branch, scenarioId),
    branchOff: removeDanglingReferencesInScopeData(branchOff, scenarioId),
  };
};

export const removeDanglingReferencesInScopeData = <T extends APIScopeData>(
  scopeData: T,
  scenarioId: ArdoqId | null
): T => {
  const { references, components } = scopeData;
  return {
    ...scopeData,
    references: removeDanglingReferences(references, components, scenarioId),
  };
};

// A dangling reference is a references where source and target are not
// in the scope data's components array.
// They don't belong in the scope data per definition.
// They are caused by a bug somewhere on the BE, but for now we remove them on
// the FE.
// Some more context in https://ardoqcom.atlassian.net/browse/ARD-11407.
const removeDanglingReferences = (
  references: APIReferenceAttributes[],
  components: APIComponentAttributes[],
  scenarioId: ArdoqId | null
) => {
  const componentIds = new Set(components.map(({ _id }) => _id));
  const [sanitizedReferences, danglingReferences] = partition(
    references,
    ({ source, target }) => componentIds.has(source) && componentIds.has(target)
  );
  if (danglingReferences.length > 0) {
    logError(Error('Scope data with dangling reference'), null, {
      scenarioId,
      danglingReferencesCount: danglingReferences.length,
    });
  }
  return sanitizedReferences;
};
